import {createSlice, PayloadAction} from '@reduxjs/toolkit';
import {
  PatientAdminSliceInitialState,
  APIResponse,
  GetCategoryListRequestPayload,
  GetCategoryListSuccessResponse,
  AddCategoryRequestPayload,
  AddCategorySuccessResponse,
  EditCategoryRequestPayload,
  EditCategorySuccessResponse,
  AddFilterRequestPayload,
  AddFilterSuccessResponse,
  EditFilterRequestPayload,
  EditFilterSuccessResponse,
  GetPatientAdminDashboardConsultationsRequestPayload,
  GetPatientAdminDashboardConsultationsSuccessResponse,
  GetPopularSpecialistListRequestPayload,
  GetPopularSpecialistListSuccessResponse,
  GetPopularityLimitSuccessResponse,
  UpdatePopularityLimitRequestPayload,
  UpdatePopularityLimitSuccessResponse,
  OverrideSpecialistPopularityRequestPayload,
  OverrideSpecialistPopularitySuccessResponse,
  GetPatientAccountTicketsRequestPayload,
  GetPatientAccountTicketsSuccessResponse,
  ExportPatientAccountRequestPayload,
  ExportPatientAccountSuccessResponse,
  DeletePatientAccountRequestPayload,
  DeletePatientAccountSuccessResponse,
  HoldPatientAccountDeletionRequestPayload,
  HoldPatientAccountDeletionSuccessResponse,
  ResumePatientAccountDeletionRequestPayload,
  ResumePatientAccountDeletionSuccessResponse,
} from '@/interface';

const initialState: PatientAdminSliceInitialState = {
  isLoading: false,
  patientAccountTicketLoading: false,
  patientAdminSuccess: false,
  patientAdminFailure: false,
  categoryManagement: {
    categories: [],
    currentPage: 0,
    totalPages: 0,
    totalResults: 0,
    nextPage: null,
  },
  filterManagement: {
    chargesFilters: [],
    experienceFilters: [],
    ratingFilters: [],
  },

  dashboard:{
    consultations: [],
    next_page: null,
    current_page: 0,
    total_pages: 0,
    total_results: 0,
  },
  popularSpecialistManagement: {
    specialists: [],
    currentPage: 0,
    totalPages: 0,
    totalResults: 0,
    nextPage: null,
  },
  popularityLimit: null,
  patientAccountTickets: {
    tickets: [],
    next_page: null,
    current_page: 0,
    total_pages: 0,
    total_results: 0,
  },
};

const patientAdminSlice = createSlice({
  name: 'patientAdmin',
  initialState,
  reducers: {
    /**
     * get category list
     */
    getCategoryListRequest: (state, action: PayloadAction<GetCategoryListRequestPayload>) => {
      state.isLoading = true;
      state.patientAdminSuccess = false;
      state.patientAdminFailure = false;
    },
    getCategoryListSuccess: (state, action: PayloadAction<GetCategoryListSuccessResponse>) => {
      state.isLoading = false;
      state.patientAdminSuccess = action.payload.status_code;
      state.categoryManagement.categories = action.payload.data.category;
      state.categoryManagement.totalPages = action.payload.data.total_page;
      state.categoryManagement.totalResults = action.payload.data.total_result;
      state.categoryManagement.currentPage = action.payload.data.current_page;
      state.categoryManagement.nextPage = action.payload.data.next_page;
    },
    getCategoryListFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.patientAdminFailure = action.payload.status_code;
      if (['E-10054', 'E-10044'].includes(action.payload.status_code)) {
        state.categoryManagement.categories = [];
        state.categoryManagement.totalPages = 0;
        state.categoryManagement.totalResults = 0;
        state.categoryManagement.currentPage = 0;
        state.categoryManagement.nextPage = null;
      }
    },

    /**
     * add category
     */
    addCategoryRequest: (state, action: PayloadAction<AddCategoryRequestPayload>) => {
      state.isLoading = true;
      state.patientAdminSuccess = false;
      state.patientAdminFailure = false;
    },
    addCategorySuccess: (state, action: PayloadAction<AddCategorySuccessResponse>) => {
      state.isLoading = false;
      state.patientAdminSuccess = action.payload.status_code;
    },
    addCategoryFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.patientAdminFailure = action.payload.status_code;
    },

    /**
     * edit category
     */
    editCategoryRequest: (state, action: PayloadAction<EditCategoryRequestPayload>) => {
      state.isLoading = true;
      state.patientAdminSuccess = false;
      state.patientAdminFailure = false;
    },
    editCategorySuccess: (state, action: PayloadAction<EditCategorySuccessResponse>) => {
      state.isLoading = false;
      state.patientAdminSuccess = action.payload.status_code;
    },
    editCategoryFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.patientAdminFailure = action.payload.status_code;
    },

    /**
     * get filter options
     */
    getFilterOptionsRequest: state => {
      state.isLoading = true;
      state.patientAdminSuccess = false;
      state.patientAdminFailure = false;
    },
    getFilterOptionsSuccess: (state, action: PayloadAction<{status: string; status_code: string; data: {experience: any[]; rating: any[]; charge: any[]}}>) => {
      state.isLoading = false;
      state.patientAdminSuccess = action.payload.status_code;
      state.filterManagement = {
        experienceFilters: action.payload.data.experience,
        ratingFilters: action.payload.data.rating,
        chargesFilters: action.payload.data.charge,
      };
    },
    getFilterOptionsFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.patientAdminFailure = action.payload.status_code;
    },

    /**
     * add filter
     */
    addFilterRequest: (state, action: PayloadAction<AddFilterRequestPayload>) => {
      state.isLoading = true;
      state.patientAdminSuccess = false;
      state.patientAdminFailure = false;
    },
    addFilterSuccess: (state, action: PayloadAction<AddFilterSuccessResponse>) => {
      state.isLoading = false;
      state.patientAdminSuccess = action.payload.status_code;
    },
    addFilterFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.patientAdminFailure = action.payload.status_code;
    },

    /**
     * edit filter
     */
    editFilterRequest: (state, action: PayloadAction<EditFilterRequestPayload>) => {
      state.isLoading = true;
      state.patientAdminSuccess = false;
      state.patientAdminFailure = false;
    },
    editFilterSuccess: (state, action: PayloadAction<EditFilterSuccessResponse>) => {
      state.isLoading = false;
      state.patientAdminSuccess = action.payload.status_code;
    },
    editFilterFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.patientAdminFailure = action.payload.status_code;
    },

    /**
     * get dashboard consultations
     */
    getDashboardConsultationsRequest: (state, action: PayloadAction<GetPatientAdminDashboardConsultationsRequestPayload>) => {
      // Ensure dashboard exists
      if (!state.dashboard) {
        state.dashboard = {
          consultations: [],
          next_page: null,
          current_page: 0,
          total_pages: 0,
          total_results: 0,
        };
      }
      state.isLoading = true;
      state.patientAdminSuccess = false;
      state.patientAdminFailure = false;
    },
    getDashboardConsultationsSuccess: (state, action: PayloadAction<GetPatientAdminDashboardConsultationsSuccessResponse>) => {
      // Ensure dashboard exists
      if (!state.dashboard) {
        state.dashboard = {
          consultations: [],
          next_page: null,
          current_page: 0,
          total_pages: 0,
          total_results: 0,
        };
      }
      state.isLoading = false;
      state.patientAdminSuccess = action.payload.status_code;
      state.dashboard.consultations = action.payload.data.consultations;
      state.dashboard.next_page = action.payload.data.next_page;
      state.dashboard.current_page = action.payload.data.current_page;
      state.dashboard.total_pages = action.payload.data.total_pages;
      state.dashboard.total_results = action.payload.data.total_results;
    },
    getDashboardConsultationsFailure: (state, action: PayloadAction<APIResponse>) => {
      // Ensure dashboard exists
      if (!state.dashboard) {
        state.dashboard = {
          consultations: [],
          next_page: null,
          current_page: 0,
          total_pages: 0,
          total_results: 0,
        };
      }
      state.isLoading = false;
      state.patientAdminFailure = action.payload.status_code;
      if (['E-10044', 'E-10054'].includes(action.payload.status_code)) {
        state.dashboard.consultations = [];
        state.dashboard.next_page = null;
        state.dashboard.current_page = 0;
        state.dashboard.total_pages = 0;
        state.dashboard.total_results = 0;
      }
    },

    /**
     * reset messages
     */
    resetPatientAdminMessage: state => {
      state.patientAdminFailure = false;
      state.patientAdminSuccess = false;
    },

    cancelPatientAdminRequest: state => {
      state.isLoading = false;
      state.patientAccountTicketLoading = false;
    },

    /**
     * get popular specialist list
     */
    getPopularSpecialistListRequest: (state, action: PayloadAction<GetPopularSpecialistListRequestPayload>) => {
      // Ensure popularSpecialistManagement exists
      if (!state.popularSpecialistManagement) {
        state.popularSpecialistManagement = {
          specialists: [],
          currentPage: 0,
          totalPages: 0,
          totalResults: 0,
          nextPage: null,
        };
      }
      state.isLoading = true;
      state.patientAdminSuccess = false;
      state.patientAdminFailure = false;
    },
    getPopularSpecialistListSuccess: (state, action: PayloadAction<GetPopularSpecialistListSuccessResponse>) => {
      // Ensure popularSpecialistManagement exists
      if (!state.popularSpecialistManagement) {
        state.popularSpecialistManagement = {
          specialists: [],
          currentPage: 0,
          totalPages: 0,
          totalResults: 0,
          nextPage: null,
        };
      }
      state.isLoading = false;
      state.patientAdminSuccess = action.payload.status_code;
      state.popularSpecialistManagement.specialists = action.payload.data.specialists;
      state.popularSpecialistManagement.totalPages = action.payload.data.total_pages;
      state.popularSpecialistManagement.totalResults = action.payload.data.total_results;
      state.popularSpecialistManagement.currentPage = action.payload.data.current_page;
      state.popularSpecialistManagement.nextPage = action.payload.data.next_page;
    },
    getPopularSpecialistListFailure: (state, action: PayloadAction<APIResponse>) => {
      // Ensure popularSpecialistManagement exists
      if (!state.popularSpecialistManagement) {
        state.popularSpecialistManagement = {
          specialists: [],
          currentPage: 0,
          totalPages: 0,
          totalResults: 0,
          nextPage: null,
        };
      }
      state.isLoading = false;
      state.patientAdminFailure = action.payload?.status_code || 'E-10044';
      if (['E-10054', 'E-10044'].includes(action.payload?.status_code || '')) {
        state.popularSpecialistManagement.specialists = [];
        state.popularSpecialistManagement.totalPages = 0;
        state.popularSpecialistManagement.totalResults = 0;
        state.popularSpecialistManagement.currentPage = 0;
        state.popularSpecialistManagement.nextPage = null;
      }
    },

    /**
     * get popularity limit
     */
    getPopularityLimitRequest: (state) => {
      state.isLoading = true;
      state.patientAdminSuccess = false;
      state.patientAdminFailure = false;
    },
    getPopularityLimitSuccess: (state, action: PayloadAction<GetPopularityLimitSuccessResponse>) => {
      state.isLoading = false;
      state.patientAdminSuccess = action.payload.status_code;
      state.popularityLimit = action.payload.data.limit;
    },
    getPopularityLimitFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.patientAdminFailure = action.payload?.status_code || 'E-10044';
    },

    /**
     * update popularity limit
     */
    updatePopularityLimitRequest: (state, action: PayloadAction<UpdatePopularityLimitRequestPayload>) => {
      state.isLoading = true;
      state.patientAdminSuccess = false;
      state.patientAdminFailure = false;
    },
    updatePopularityLimitSuccess: (state, action: PayloadAction<UpdatePopularityLimitSuccessResponse>) => {
      state.isLoading = false;
      state.patientAdminSuccess = action.payload.status_code;
      state.popularityLimit = action.payload.data.limit;
    },
    updatePopularityLimitFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.patientAdminFailure = action.payload?.status_code || 'E-10044';
    },

    /**
     * override specialist popularity
     */
    overrideSpecialistPopularityRequest: (state, action: PayloadAction<OverrideSpecialistPopularityRequestPayload>) => {
      state.isLoading = true;
      state.patientAdminSuccess = false;
      state.patientAdminFailure = false;
    },
    overrideSpecialistPopularitySuccess: (state, action: PayloadAction<OverrideSpecialistPopularitySuccessResponse>) => {
      state.isLoading = false;
      state.patientAdminSuccess = action.payload.status_code;
      // Update the specialist in the list
      if (state.popularSpecialistManagement?.specialists) {
        const index = state.popularSpecialistManagement.specialists.findIndex(s => s.id === action.payload.data.id);
        if (index !== -1) {
          state.popularSpecialistManagement.specialists[index].admin_overridden = action.payload.data.admin_overridden;
          state.popularSpecialistManagement.specialists[index].final_popularity = action.payload.data.final_popularity;
        }
      }
    },
    overrideSpecialistPopularityFailure: (state, action: PayloadAction<APIResponse>) => {
      state.isLoading = false;
      state.patientAdminFailure = action.payload?.status_code || 'E-10044';
    },

    /**
     * get patient account tickets
     */
    getPatientAccountTicketsRequest: (state, action: PayloadAction<GetPatientAccountTicketsRequestPayload>) => {
      if (!state.patientAccountTickets) {
        state.patientAccountTickets = {
          tickets: [],
          next_page: null,
          current_page: 0,
          total_pages: 0,
          total_results: 0,
        };
      }
      state.isLoading = true;
      state.patientAdminSuccess = false;
      state.patientAdminFailure = false;
    },
    getPatientAccountTicketsSuccess: (state, action: PayloadAction<GetPatientAccountTicketsSuccessResponse>) => {
      if (!state.patientAccountTickets) {
        state.patientAccountTickets = {
          tickets: [],
          next_page: null,
          current_page: 0,
          total_pages: 0,
          total_results: 0,
        };
      }
      state.isLoading = false;
      state.patientAdminSuccess = action.payload.status_code;
      state.patientAccountTickets.tickets = action.payload.data.tickets;
      state.patientAccountTickets.next_page = action.payload.data.next_page;
      state.patientAccountTickets.current_page = action.payload.data.current_page;
      state.patientAccountTickets.total_pages = action.payload.data.total_pages;
      state.patientAccountTickets.total_results = action.payload.data.total_results;
    },
    getPatientAccountTicketsFailure: (state, action: PayloadAction<APIResponse>) => {
      if (!state.patientAccountTickets) {
        state.patientAccountTickets = {
          tickets: [],
          next_page: null,
          current_page: 0,
          total_pages: 0,
          total_results: 0,
        };
      }
      state.isLoading = false;
      state.patientAdminFailure = action.payload.status_code;
      if (['E-10044', 'E-10054'].includes(action.payload.status_code)) {
        state.patientAccountTickets.tickets = [];
        state.patientAccountTickets.next_page = null;
        state.patientAccountTickets.current_page = 0;
        state.patientAccountTickets.total_pages = 0;
        state.patientAccountTickets.total_results = 0;
      }
    },

    /**
     * export patient account
     */
    exportPatientAccountRequest: (state, action: PayloadAction<ExportPatientAccountRequestPayload>) => {
      state.patientAccountTicketLoading = true;
      state.patientAdminSuccess = false;
      state.patientAdminFailure = false;
    },
    exportPatientAccountSuccess: (state, action: PayloadAction<ExportPatientAccountSuccessResponse>) => {
      state.patientAccountTicketLoading = false;
      state.patientAdminSuccess = action.payload.status_code;
    },
    exportPatientAccountFailure: (state, action: PayloadAction<APIResponse>) => {
      state.patientAccountTicketLoading = false;
      state.patientAdminFailure = action.payload.status_code;
    },

    /**
     * delete patient account
     */
    deletePatientAccountRequest: (state, action: PayloadAction<DeletePatientAccountRequestPayload>) => {
      state.patientAccountTicketLoading = true;
      state.patientAdminSuccess = false;
      state.patientAdminFailure = false;
    },
    deletePatientAccountSuccess: (state, action: PayloadAction<DeletePatientAccountSuccessResponse>) => {
      state.patientAccountTicketLoading = false;
      state.patientAdminSuccess = action.payload.status_code;
    },
    deletePatientAccountFailure: (state, action: PayloadAction<APIResponse>) => {
      state.patientAccountTicketLoading = false;
      state.patientAdminFailure = action.payload.status_code;
    },

    /**
     * hold patient account deletion
     */
    holdPatientAccountDeletionRequest: (state, action: PayloadAction<HoldPatientAccountDeletionRequestPayload>) => {
      state.patientAccountTicketLoading = true;
      state.patientAdminSuccess = false;
      state.patientAdminFailure = false;
    },
    holdPatientAccountDeletionSuccess: (state, action: PayloadAction<HoldPatientAccountDeletionSuccessResponse>) => {
      state.patientAccountTicketLoading = false;
      state.patientAdminSuccess = action.payload.status_code;
    },
    holdPatientAccountDeletionFailure: (state, action: PayloadAction<APIResponse>) => {
      state.patientAccountTicketLoading = false;
      state.patientAdminFailure = action.payload.status_code;
    },

    /**
     * resume patient account deletion
     */
    resumePatientAccountDeletionRequest: (state, action: PayloadAction<ResumePatientAccountDeletionRequestPayload>) => {
      state.patientAccountTicketLoading = true;
      state.patientAdminSuccess = false;
      state.patientAdminFailure = false;
    },
    resumePatientAccountDeletionSuccess: (state, action: PayloadAction<ResumePatientAccountDeletionSuccessResponse>) => {
      state.patientAccountTicketLoading = false;
      state.patientAdminSuccess = action.payload.status_code;
    },
    resumePatientAccountDeletionFailure: (state, action: PayloadAction<APIResponse>) => {
      state.patientAccountTicketLoading = false;
      state.patientAdminFailure = action.payload.status_code;
    },
  },
});

export const {
  // get category list
  getCategoryListRequest,
  getCategoryListSuccess,
  getCategoryListFailure,

  // add category
  addCategoryRequest,
  addCategorySuccess,
  addCategoryFailure,

  // edit category
  editCategoryRequest,
  editCategorySuccess,
  editCategoryFailure,

  // get filter options
  getFilterOptionsRequest,
  getFilterOptionsSuccess,
  getFilterOptionsFailure,

  // add filter
  addFilterRequest,
  addFilterSuccess,
  addFilterFailure,

  // edit filter
  editFilterRequest,
  editFilterSuccess,
  editFilterFailure,

  // get dashboard consultations
  getDashboardConsultationsRequest,
  getDashboardConsultationsSuccess,
  getDashboardConsultationsFailure,

  
  getPopularSpecialistListRequest,
  getPopularSpecialistListSuccess,
  getPopularSpecialistListFailure,
  getPopularityLimitRequest,
  getPopularityLimitSuccess,
  getPopularityLimitFailure,
  updatePopularityLimitRequest,
  updatePopularityLimitSuccess,
  updatePopularityLimitFailure,
  overrideSpecialistPopularityRequest,
  overrideSpecialistPopularitySuccess,
  overrideSpecialistPopularityFailure,

  // get patient account tickets
  getPatientAccountTicketsRequest,
  getPatientAccountTicketsSuccess,
  getPatientAccountTicketsFailure,

  // export patient account
  exportPatientAccountRequest,
  exportPatientAccountSuccess,
  exportPatientAccountFailure,

  // delete patient account
  deletePatientAccountRequest,
  deletePatientAccountSuccess,
  deletePatientAccountFailure,

  // hold patient account deletion
  holdPatientAccountDeletionRequest,
  holdPatientAccountDeletionSuccess,
  holdPatientAccountDeletionFailure,

  // resume patient account deletion
  resumePatientAccountDeletionRequest,
  resumePatientAccountDeletionSuccess,
  resumePatientAccountDeletionFailure,

  // reset messages
  resetPatientAdminMessage,
  cancelPatientAdminRequest,
} = patientAdminSlice.actions;

export default patientAdminSlice.reducer;

